<script setup lang="ts">
import Sliderup from '@components/Sliderup.vue';
import IconLogo from '@icons/ph_logo.svg';
import Icon from '@components/Icon.vue';
import { URL_HOME, URL_LOGOUT } from '@tools/routes/routes';
import { type Component, computed } from 'vue';
import { AccountProfile } from '@enums/accountProfile.ts';
import { storeToRefs } from 'pinia';
import { useUserProfileStore } from '@stores/useUserProfileStore.ts';
import MobilePlusUnqualifiedMenu from '@layout/nav-v2/mobile/components/menus/MobilePlusUnqualifiedMenu.vue';
import MobilePlusSellerMenu from '@layout/nav-v2/mobile/components/menus/MobilePlusSellerMenu.vue';
import MobilePlusBuyerMenu from '@layout/nav-v2/mobile/components/menus/MobilePlusBuyerMenu.vue';
import MenuList from '@layout/nav-v2/mobile/components/MenuList.vue';
import ButtonLink from '@components/ButtonLink.vue';
import { trans } from '@elements/translation.ts';
import MenuAccountManagerContact from '@layout/nav-v2/MenuAccountManagerContact.vue';
import MobilePlusAnonymousMenu from '@layout/nav-v2/mobile/components/menus/MobilePlusAnonymousMenu.vue';
import { MenuItemGroup as MenuItemGroupEnum } from '@enums/menuItemGroups.ts';
import IconQuestionCircle from '@icons/ph_question-circle.svg';
import PlusMenuItemGroup from '@layout/nav-v2/mobile/components/PlusMenuItemGroup.vue';
import MobilePlusHelpMenu from '@layout/nav-v2/mobile/components/menus/MobilePlusHelpMenu.vue';
import { useNavbarStore } from '@stores/layout/useNavbarStore';

defineProps<{ isOpen: boolean }>();

const { userProfile, currentProfile, user } = storeToRefs(useUserProfileStore());

const { isCurrentDropdownOpen, setCurrentDropdownOpen } = useNavbarStore();

const menu = computed<Component>(() => {
  if (userProfile.value === AccountProfile.ANONYMOUS) {
    return MobilePlusAnonymousMenu;
  }

  if (userProfile.value === AccountProfile.UNQUALIFIED) {
    return MobilePlusUnqualifiedMenu;
  }

  if (userProfile.value === AccountProfile.BOTH) {
    return currentProfile.value === AccountProfile.SELLER ? MobilePlusSellerMenu : MobilePlusBuyerMenu;
  }

  if (userProfile.value === AccountProfile.SELLER) {
    return MobilePlusSellerMenu;
  }

  return MobilePlusBuyerMenu;
});
</script>

<template>
  <Sliderup :is-opened="isOpen">
    <template #header>
      <a :href="URL_HOME">
        <Icon
          :svg="IconLogo"
          width="64"
          height="34"
        />
      </a>
    </template>

    <template #body>
      <div
        :class="[
          'mt-6',
          'px-4 pt-0 pb-20',
          'overflow-scroll',
        ]"
      >
        <MenuList class="flex flex-col gap-y-2">
          <component :is="menu" />
        </MenuList>

        <MenuAccountManagerContact
          v-if="user"
          class="mt-4"
          :account-manager="user.account_manager ?? null"
        />

        <PlusMenuItemGroup
          class="mt-4"
          :is-dropdown-open="isCurrentDropdownOpen(MenuItemGroupEnum.HELP)"
          @toggle-dropdown="(isOpenDropdown) => setCurrentDropdownOpen(isOpenDropdown ? MenuItemGroupEnum.HELP : null)"
        >
          <template #icon="{ isActive }">
            <Icon
              :svg="IconQuestionCircle"
              width="24"
              height="24"
              :fill="isActive ? 'black' : 'gray-darker'"
            />
          </template>

          <template #text>
            {{ trans('user_menu.help') }}
          </template>

          <template #submenus>
            <MobilePlusHelpMenu />
          </template>
        </PlusMenuItemGroup>

        <ButtonLink
          :href="URL_LOGOUT"
          class="mt-6"
        >
          {{ trans('global.disconnect') }}
        </ButtonLink>
      </div>
    </template>
  </Sliderup>
</template>
